import { useEffect, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import Sidebar from '../components/layout/sidebar/sidebar'
import { MainView } from '../components/layout/main'
import {
    FundableClientsStat,
    AdminReportsSection,
    PayableClaimsProgress,
    SectionVisibilityGroup,
    IntroducerClientActions,
} from '../components/reusable'
import type { AdminReportOption } from '../components/reusable'
import api from '../helpers/apiRequester'
import { getLoginUrl } from '../helpers/authConfig'
import { StatsView } from '../components/views'
import Modal from '../components/reusable/modal'
import { ViewChartDataModalContent } from '../components/modalContent/viewChartDataContent'
import { Page, PageHeader } from '../ui'

// Admin-only eligibility exports (Sentinel users only — the section hides itself
// for everyone else, and each reportType is independently gated server-side).
const SENTINEL_MATTERS_REPORTS: AdminReportOption[] = [
    {
        value: 'sentinel-matters-fundable-clients',
        label: 'Clients with Claims (Fundable)',
        filename: 'fundable-clients',
    },
    { value: 'sentinel-matters-signed-clients', label: 'All Clients Eligibility', filename: 'all-clients-eligibility' },
    {
        value: 'sentinel-matters-claims-eligibility',
        label: 'Claims Eligibility Breakdown',
        filename: 'claims-eligibility',
    },
]

function SentinelMattersHome() {
    const [dataReady, setDataReady] = useState(false)
    const [loading, setLoading] = useState(false)
    const [overviewData, setOverviewData] = useState<any>()
    const [modalChartData, setModalChartData] = useState<any>()
    const [modalState, setModalState] = useState(false)
    const [modalTitle, setModalTitle] = useState('')
    const [modalDate, setModalDate] = useState('')

    useEffect(() => {
        if (modalChartData != null) {
            setModalState(true)
        }
    }, [modalChartData])

    useEffect(() => {
        if (!dataReady && !loading) {
            setLoading(true)
            getData()
        }
    }, [dataReady, loading])

    const getData = async () => {
        Loading.circle('Loading...')
        try {
            const apiResponse = await api({ url: '/sentinel-matters-leads' })
            const data = apiResponse.data

            setOverviewData(data)
            setDataReady(true)
            Loading.remove()
        } catch (error: any) {
            console.log(error)
            Loading.remove()
            if (error?.response?.status == 401) {
                Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                    window.location.href = getLoginUrl()
                })
            } else {
                window.location.href = getLoginUrl()
            }
        }
    }

    const prepChartData = (data: any) => {
        setModalChartData(data.dataResults)
        setModalTitle(data.title)
        setModalDate(data.date)
    }

    const toggleModal = () => {
        setModalChartData(null)
        setModalState(false)
    }

    return (
        <div className="home">
            <Sidebar />
            <MainView>
                <Page>
                    <PageHeader eyebrow="PCP Introducers" title={'SentinelMatters Lead Tracker'} />
                    <SectionVisibilityGroup audience="introducer" introducer="SentinelMatters">
                        {dataReady ? (
                            <StatsView
                                stats={overviewData.stats}
                                chartData={overviewData?.chartData}
                                dataGroup={overviewData.group}
                                handleDataModal={prepChartData}
                                fundableIntroducer="SentinelMatters"
                                excludeTitles={['Claims Progress']}
                            />
                        ) : (
                            <></>
                        )}
                        <PayableClaimsProgress introducer="SentinelMatters" />
                    </SectionVisibilityGroup>
                    <SectionVisibilityGroup audience="admin" introducer="SentinelMatters">
                        <FundableClientsStat introducer="SentinelMatters" />
                        <AdminReportsSection reportOptions={SENTINEL_MATTERS_REPORTS} filenamePrefix="sentinel-matters" />
                        {dataReady ? (
                            <StatsView
                                stats={overviewData.stats}
                                chartData={overviewData?.chartData}
                                dataGroup={overviewData.group}
                                handleDataModal={prepChartData}
                                onlyTitles={['Claims Progress']}
                            />
                        ) : (
                            <></>
                        )}
                        <IntroducerClientActions introducer="SentinelMatters" introducerLabel="SentinelMatters" />
                    </SectionVisibilityGroup>
                </Page>
            </MainView>
            <Modal modalState={modalState} previewModalAction={toggleModal}>
                <ViewChartDataModalContent
                    chartData={modalChartData}
                    closeModal={toggleModal}
                    chartTitle={modalTitle}
                    chartDate={modalDate}
                />
            </Modal>
        </div>
    )
}

export default SentinelMattersHome
